import { useState, useEffect } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import './SMEStyles.css';

const CheckCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const SMEVariationReview = () => {
  const { showToast } = useToast();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: '', comments: '' });

  useEffect(() => {
    fetchPendingReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingReviews = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/question-variations/sme/pending-reviews');
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showToast('Failed to load pending reviews', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (variationId, status) => {
    if (!reviewForm.comments && status === 'rejected') {
      showToast('Please provide comments for rejection', 'error');
      return;
    }

    try {
      await API.post(`/question-variations/variations/${variationId}/review`, {
        status,
        comments: reviewForm.comments
      });

      showToast(`Variation ${status === 'approved' ? 'approved' : 'rejected'} successfully!`, 'success');

      setReviewForm({ status: '', comments: '' });
      setSelectedReview(null);
      await fetchPendingReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      showToast(error.response?.data?.message || 'Failed to submit review', 'error');
    }
  };

  const statusBadgeClass = (status) =>
    status === 'approved' ? 'sme-badge--success'
      : status === 'rejected' ? 'sme-badge--danger'
        : 'sme-badge--warning';

  return (
    <div className="sme-page">
      <div className="sme-page-header">
        <div>
          <h1>Variation Reviews</h1>
          <p>Review question variations submitted by examiners</p>
        </div>
      </div>

      {loading ? (
        <div className="sme-spinner-wrap">
          <div className="sme-spinner" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="sme-card">
          <div className="sme-empty">
            <CheckCircleIcon />
            <h3>No pending reviews</h3>
            <p>All caught up! New variations will appear here for review.</p>
          </div>
        </div>
      ) : (
        <div className="sme-card-grid">
          {reviews.map((review) => (
            <div key={review.review_id} className="sme-card">
              <div className="sme-card-header">
                <div>
                  <h2>Variation {review.variation_number}</h2>
                  <p>From: {review.examiner_name} &bull; {review.marks} marks &bull; {review.question_type}</p>
                </div>
                <span className={`sme-badge ${statusBadgeClass(review.status)}`}>{review.status}</span>
              </div>

              <div className="sme-card-body">
                <div className="sme-field">
                  <label className="sme-field-label">Question</label>
                  <p className="sme-field-value">{review.question_text}</p>
                </div>

                {review.options && (
                  <div className="sme-field">
                    <label className="sme-field-label">Options</label>
                    <div style={{ marginLeft: '0.5rem' }}>
                      {JSON.parse(review.options).map((opt, idx) => (
                        <div key={idx} className="sme-option-row">{opt}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="sme-field">
                  <label className="sme-field-label">Correct Answer</label>
                  <p className="sme-answer-box">{review.correct_answer}</p>
                </div>

                {review.status === 'pending' && (
                  <div className="sme-divider-top">
                    <div className="sme-form-group">
                      <label className="sme-form-label">Comments (optional for approval, required for rejection)</label>
                      <textarea
                        className="sme-form-textarea"
                        value={selectedReview === review.review_id ? reviewForm.comments : ''}
                        onChange={(e) => {
                          setSelectedReview(review.review_id);
                          setReviewForm({ ...reviewForm, comments: e.target.value });
                        }}
                        placeholder="Add your feedback here..."
                        rows="3"
                      />
                    </div>

                    <div className="sme-actions-end">
                      <button onClick={() => handleReview(review.variation_id, 'rejected')} className="sme-btn sme-btn--danger">
                        Reject
                      </button>
                      <button onClick={() => handleReview(review.variation_id, 'approved')} className="sme-btn sme-btn--success">
                        Approve
                      </button>
                    </div>
                  </div>
                )}

                {review.status !== 'pending' && review.comments && (
                  <div className="sme-divider-top">
                    <label className="sme-field-label">Your Comments</label>
                    <p className="sme-muted" style={{ margin: 0, fontStyle: 'italic' }}>{review.comments}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SMEVariationReview;
